import React, { useState } from 'react';
import { Scale, Clock, Shield, AlertCircle, CheckCircle, FileText, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CalculadoraModal from './CalculadoraModal';

const AreasAtuacao = () => {
  const [modalAberto, setModalAberto] = useState(null);
  const [calculadoraAberta, setCalculadoraAberta] = useState(null);

  const areas = [
    {
      id: 'rescisao',
      icon: Scale,
      titulo: 'Rescisão Indireta',
      descricao: 'Defesa dos seus direitos quando o empregador comete faltas graves',
      detalhes: {
        pergunta: 'Você está passando por:',
        situacoes: [
          'Atraso salarial frequente?',
          'Assédio moral?',
          'Falta de recolhimento de FGTS?',
          'Exigência de funções além do contrato?'
        ],
        botao: 'Quero analisar meu caso'
      }
    },
    {
      id: 'horas',
      icon: Clock,
      titulo: 'Horas Extras',
      descricao: 'Cálculo e cobrança de horas extras não pagas',
      complemento: 'Trabalho aos domingos pode gerar adicional de 100%',
      detalhes: {
        pergunta: 'Situações comuns:',
        situacoes: [
          'Trabalha além da jornada sem receber?',
          'Faz hora extra mas não é pago corretamente?',
          'Trabalha aos finais de semana?',
          'Não tem intervalo para descanso?'
        ],
        botao: 'Simular horas extras'
      }
    },
    {
      id: 'danos',
      icon: AlertCircle,
      titulo: 'Danos Morais',
      descricao: 'Reparação por situações abusivas no ambiente de trabalho',
      detalhes: {
        pergunta: 'Exemplos de situações abusivas:',
        situacoes: [
          'Humilhações públicas',
          'Exposição vexatória',
          'Metas abusivas e inalcançáveis',
          'Discriminação ou preconceito'
        ],
        botao: 'Avaliar meu caso'
      }
    },
    {
      id: 'insalubridade',
      icon: Shield,
      titulo: 'Insalubridade',
      descricao: 'Reconhecimento e pagamento de adicional conforme legislação',
      detalhes: {
        pergunta: 'Trabalha em condições insalubres?',
        situacoes: [
          'Trabalhos em banheiros de grande circulação',
          'Contato com agentes químicos ou biológicos nocivos',
          'Exposição a ruído excessivo',
          'Ambientes com calor ou frio extremos'
        ],
        botao: 'Verificar direito'
      }
    },
    {
      id: 'calculo',
      icon: FileText,
      titulo: 'Cálculo de Rescisão',
      descricao: 'Análise e cálculo preciso de verbas rescisórias',
      complemento: 'Foi demitido? Calcule aqui se sua rescisão está correta',
      detalhes: {
        pergunta: 'Recebeu tudo que tinha direito?',
        situacoes: [
          'Saldo de salário',
          'Férias proporcionais + 1/3',
          '13º salário proporcional',
          'Multa de 40% do FGTS'
        ],
        botao: 'Simular agora'
      }
    },
    {
      id: 'trabalhista',
      icon: Scale,
      titulo: 'Direito do Trabalho',
      descricao: 'Assessoria completa em todas as questões trabalhistas',
      detalhes: {
        pergunta: 'Outras questões trabalhistas:',
        situacoes: [
          'Desvio de função',
          'Acidente de trabalho',
          'Doença ocupacional',
          'Estabilidade (gestante, acidente, CIPA)'
        ],
        botao: 'Consultar especialista'
      }
    }
  ];

  const abrirModal = (areaId) => {
    setModalAberto(areaId);
  };

  const fecharModal = () => {
    setModalAberto(null);
  };

  const abrirCalculadora = (tipo) => {
    setCalculadoraAberta(tipo);
    fecharModal();
  };

  const areaAtual = areas.find(a => a.id === modalAberto);

  return (
    <>
      <section className="py-20 px-4 bg-off-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-oxford mb-4">
              Atuação
            </h2>
            <div className="w-32 h-1 bg-champagne mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Especialização em Advocacia Trabalhista com atendimento personalizado e técnica apurada
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {areas.map((area) => {
              const Icon = area.icon;
              return (
                <Card 
                  key={area.id} 
                  className="card-glass border-t-4 border-champagne cursor-pointer group"
                  onClick={() => abrirModal(area.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="bg-champagne/20 p-3 rounded-lg group-hover:bg-champagne/30 transition-colors">
                        <Icon className="w-6 h-6 text-champagne" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-serif text-xl font-semibold text-oxford mb-3 group-hover:text-champagne transition-colors">
                          {area.titulo}
                        </h3>
                        <p className="text-gray-600 leading-relaxed mb-2">
                          {area.descricao}
                        </p>
                        {area.complemento && (
                          <p className="text-sm text-champagne font-semibold mt-3">
                            {area.complemento}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-oxford font-medium flex items-center gap-2 group-hover:text-champagne transition-colors">
                      Saiba mais
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modal de Detalhes */}
      {areaAtual && (
        <Dialog open={!!modalAberto} onOpenChange={fecharModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-serif text-2xl text-oxford flex items-center gap-3">
                  {React.createElement(areaAtual.icon, { className: 'w-7 h-7 text-champagne' })}
                  {areaAtual.titulo}
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fecharModal}
                  className="rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <p className="text-gray-700 text-lg leading-relaxed">
                {areaAtual.descricao}
              </p>

              {areaAtual.complemento && (
                <div className="bg-champagne/10 border-l-4 border-champagne p-4 rounded">
                  <p className="text-oxford font-semibold">
                    {areaAtual.complemento}
                  </p>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-oxford mb-4 text-lg">
                  {areaAtual.detalhes.pergunta}
                </h4>
                <ul className="space-y-3">
                  {areaAtual.detalhes.situacoes.map((situacao, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-champagne flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{situacao}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-4 space-y-3">
                {(areaAtual.id === 'rescisao' || areaAtual.id === 'horas' || areaAtual.id === 'calculo') && (
                  <Button 
                    onClick={() => abrirCalculadora(areaAtual.id === 'horas' ? 'horas' : 'rescisao')}
                    className="btn-champagne w-full py-6 text-lg font-semibold"
                  >
                    {areaAtual.detalhes.botao}
                  </Button>
                )}
                
                <Button 
                  onClick={() => window.open('https://wa.me/5562996951404', '_blank')}
                  variant="outline"
                  className="w-full py-6 text-lg border-2 border-oxford text-oxford hover:bg-oxford hover:text-white"
                >
                  Falar com Dr. Leandro
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Calculadora Modal */}
      {calculadoraAberta && (
        <CalculadoraModal 
          tipo={calculadoraAberta}
          isOpen={!!calculadoraAberta}
          onClose={() => setCalculadoraAberta(null)}
        />
      )}
    </>
  );
};

export default AreasAtuacao;
